import { ArrowUpRight, ArrowDownRight, Minus, TrendingUp, TrendingDown, Activity, AlertCircle, CircleDot } from "lucide-react";

/** Formatting helpers */
const fmt = (v, digits = 2) => {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1000)
    return n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return n.toFixed(digits);
};
const fmtPct = (v, digits = 2) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(digits)}%`);
const fmtBigVol = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n.toFixed(0);
};
const dirClass = (v) => (v == null ? "text-slate-400" : v > 0 ? "text-emerald-400" : v < 0 ? "text-red-400" : "text-slate-400");
const DirIcon = ({ v, className = "h-3 w-3 inline" }) => {
  if (v == null) return <Minus className={className + " text-slate-500"} />;
  if (v > 0) return <ArrowUpRight className={className + " text-emerald-400"} />;
  if (v < 0) return <ArrowDownRight className={className + " text-red-400"} />;
  return <Minus className={className + " text-slate-500"} />;
};

function Panel({ title, icon, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-[#1E293B] bg-[#0F1626] flex flex-col"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#1E293B] bg-[#0B111C]">
        {icon}
        <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          {title}
        </span>
      </div>
      <div className="flex-1 p-3">{children}</div>
    </div>
  );
}

function KV({ label, value, cls = "", testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 border-b border-[#151C2C] last:border-b-0">
      <span className="mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      <span data-testid={testid} className={`mono text-xs tabular-nums ${cls || "text-slate-100"}`}>
        {value}
      </span>
    </div>
  );
}

export default function IndicatorPanel({ report }) {
  if (!report) {
    return (
      <div className="border border-[#1E293B] bg-[#0F1626] p-6 text-slate-500 mono text-xs text-center">
        No analysis available.
      </div>
    );
  }

  const pc = report.price_changes || {};
  const emas = report.emas || {};
  const macd = report.macd || {};
  const bb = report.bollinger || {};
  const adx = report.adx14 || {};
  const vol = report.volume || {};
  const vwap = report.vwap || {};
  const r52 = report.range_52w || {};
  const trend = report.trend_structure || {};
  const rsi = report.rsi14;

  const rsiZone =
    rsi == null ? "" : rsi > 70 ? "text-red-400" : rsi < 30 ? "text-emerald-400" : "text-slate-100";

  const trendBadgeCls =
    trend.structure === "bullish"
      ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
      : trend.structure === "bearish"
      ? "text-red-300 border-red-500/40 bg-red-500/10"
      : trend.structure === "neutral"
      ? "text-slate-300 border-slate-500/40 bg-slate-500/10"
      : "text-amber-300 border-amber-500/40 bg-amber-500/10";

  // Compute position within 52w range for visualizer
  let rangePct = null;
  if (r52.high && r52.low && report.price) {
    rangePct = ((report.price - r52.low) / (r52.high - r52.low)) * 100;
    rangePct = Math.max(0, Math.min(100, rangePct));
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="indicator-panel">
      {/* Price changes */}
      <Panel
        title="Price Change"
        icon={<TrendingUp className="h-3.5 w-3.5 text-cyan-400" />}
        testid="panel-price-change"
      >
        <div className="space-y-0.5">
          {[
            ["1D", pc.d1, "indicator-pc-1d"],
            ["5D", pc.d5, "indicator-pc-5d"],
            ["20D", pc.d20, "indicator-pc-20d"],
            ["60D", pc.d60, "indicator-pc-60d"],
            ["YTD", pc.ytd, "indicator-pc-ytd"],
          ].map(([label, val, tid]) => (
            <div key={label} className="flex items-center justify-between py-1 border-b border-[#151C2C] last:border-b-0">
              <span className="mono text-[10px] uppercase tracking-wider text-slate-500 w-8">{label}</span>
              <div className="flex-1 mx-2 h-1 bg-[#151C2C] relative">
                {val != null && (
                  <div
                    className={`absolute top-0 bottom-0 ${val >= 0 ? "left-1/2 bg-emerald-500/70" : "right-1/2 bg-red-500/70"}`}
                    style={{ width: `${Math.min(50, Math.abs(val) * 2)}%` }}
                  />
                )}
                <div className="absolute top-0 bottom-0 left-1/2 w-px bg-slate-600" />
              </div>
              <span data-testid={tid} className={`mono text-xs w-16 text-right ${dirClass(val)}`}>
                <DirIcon v={val} /> {fmtPct(val)}
              </span>
            </div>
          ))}
        </div>
      </Panel>

      {/* Trend / EMA */}
      <Panel
        title="Trend · EMA"
        icon={<Activity className="h-3.5 w-3.5 text-cyan-400" />}
        testid="panel-ema"
      >
        <table className="q-table">
          <thead>
            <tr>
              <th>EMA</th>
              <th className="right">Value</th>
              <th className="right">Slope</th>
              <th className="right">Δ Price</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["9", emas.ema9, "#38BDF8"],
              ["20", emas.ema20, "#FACC15"],
              ["50", emas.ema50, "#FB923C"],
              ["200", emas.ema200, "#E879F9"],
            ].map(([label, e, color]) => (
              <tr key={label}>
                <td>
                  <span className="inline-block w-2 h-2 mr-2" style={{ background: color }} />
                  <span className="text-slate-100">EMA{label}</span>
                </td>
                <td className="right text-slate-100" data-testid={`indicator-ema${label}-value`}>{fmt(e?.value)}</td>
                <td className={`right ${dirClass(e?.slope_pct_5)}`}>{e?.slope_pct_5 == null ? "—" : `${e.slope_pct_5.toFixed(2)}%`}</td>
                <td className={`right ${dirClass(e?.dist_pct)}`}>{e?.dist_pct == null ? "—" : `${e.dist_pct >= 0 ? "+" : ""}${e.dist_pct.toFixed(2)}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {/* Momentum */}
      <Panel
        title="Momentum"
        icon={<Activity className="h-3.5 w-3.5 text-cyan-400" />}
        testid="panel-momentum"
      >
        <div className="space-y-2">
          {/* RSI */}
          <div>
            <div className="flex items-center justify-between">
              <span className="mono text-[10px] uppercase tracking-wider text-slate-500">RSI(14)</span>
              <span data-testid="indicator-rsi-value" className={`mono text-sm ${rsiZone}`}>{fmt(rsi, 2)}</span>
            </div>
            <div className="mt-1.5 h-1 bg-[#151C2C] relative">
              <div className="absolute top-0 bottom-0 left-[30%] w-px bg-emerald-500/40" />
              <div className="absolute top-0 bottom-0 left-[70%] w-px bg-red-500/40" />
              {rsi != null && (
                <div
                  className="absolute top-[-3px] w-1 h-[10px] bg-cyan-400"
                  style={{ left: `calc(${Math.max(0, Math.min(100, rsi))}% - 2px)` }}
                />
              )}
            </div>
            <div className="flex justify-between mono text-[9px] text-slate-600 mt-1">
              <span>0</span><span className="text-emerald-500/70">30 OS</span><span>50</span><span className="text-red-500/70">70 OB</span><span>100</span>
            </div>
          </div>
          {/* MACD */}
          <div className="pt-2 border-t border-[#151C2C]">
            <div className="flex items-center justify-between">
              <span className="mono text-[10px] uppercase tracking-wider text-slate-500">MACD</span>
              <span data-testid="indicator-macd-value" className={`mono text-xs ${dirClass(macd.line)}`}>{fmt(macd.line, 4)}</span>
            </div>
            <div className="grid grid-cols-3 gap-1 mt-1 mono text-[10px]">
              <div className="flex flex-col"><span className="text-slate-500">Signal</span><span className="text-slate-100">{fmt(macd.signal, 4)}</span></div>
              <div className="flex flex-col"><span className="text-slate-500">Hist</span><span className={dirClass(macd.hist)}>{fmt(macd.hist, 4)}</span></div>
              <div className="flex flex-col"><span className="text-slate-500">Hist Δ5</span><span className={dirClass(macd.hist_slope_5)}>{fmt(macd.hist_slope_5, 4)}</span></div>
            </div>
          </div>
          {/* ADX */}
          <div className="pt-2 border-t border-[#151C2C]">
            <div className="flex items-center justify-between">
              <span className="mono text-[10px] uppercase tracking-wider text-slate-500">ADX(14)</span>
              <span data-testid="indicator-adx-value" className="mono text-xs text-slate-100">{fmt(adx.adx, 2)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-1 mono text-[10px]">
              <div className="flex justify-between"><span className="text-slate-500">+DI</span><span className="text-emerald-400">{fmt(adx.plus_di, 2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">-DI</span><span className="text-red-400">{fmt(adx.minus_di, 2)}</span></div>
            </div>
          </div>
        </div>
      </Panel>

      {/* Volatility */}
      <Panel
        title="Volatility"
        icon={<TrendingDown className="h-3.5 w-3.5 text-cyan-400" />}
        testid="panel-volatility"
      >
        <div className="space-y-1">
          <KV label="ATR(14)" value={fmt(report.atr14, 3)} testid="indicator-atr-value" />
          <KV label="ATR %" value={fmtPct(report.atr_pct)} testid="indicator-atr-pct" />
          <div className="pt-2 border-t border-[#151C2C]">
            <div className="mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">Bollinger (20,2)</div>
            <KV label="Upper" value={fmt(bb.upper)} cls="text-red-300" />
            <KV label="Middle" value={fmt(bb.middle)} cls="text-slate-100" />
            <KV label="Lower" value={fmt(bb.lower)} cls="text-emerald-300" />
            <KV label="Bandwidth" value={fmtPct(bb.bandwidth_pct)} />
          </div>
        </div>
      </Panel>

      {/* Volume & VWAP */}
      <Panel
        title="Volume · VWAP"
        icon={<Activity className="h-3.5 w-3.5 text-cyan-400" />}
        testid="panel-volume"
      >
        <div className="space-y-1">
          <KV label="Current Vol" value={fmtBigVol(vol.current)} testid="indicator-volume-current" />
          <KV label="20D Avg Vol" value={fmtBigVol(vol.avg_20)} />
          <KV
            label="RVOL"
            value={vol.rvol == null ? "—" : `${vol.rvol.toFixed(2)}x`}
            cls={vol.rvol > 1.5 ? "text-emerald-300" : vol.rvol < 0.8 ? "text-slate-500" : "text-slate-100"}
            testid="indicator-rvol"
          />
          <KV label="Vol Δ 20D" value={fmtPct(vol.acceleration_pct)} cls={dirClass(vol.acceleration_pct)} />
          <div className="pt-2 border-t border-[#151C2C]">
            <KV label="VWAP (Roll 20)" value={fmt(vwap.rolling_20)} />
            <KV label="VWAP (Session)" value={fmt(vwap.session_intraday)} testid="indicator-vwap-session" />
          </div>
        </div>
      </Panel>

      {/* 52-week range */}
      <Panel
        title="52-Week Range"
        icon={<CircleDot className="h-3.5 w-3.5 text-cyan-400" />}
        testid="panel-52w"
      >
        <div className="space-y-1">
          <KV label="52W High" value={fmt(r52.high)} cls="text-emerald-300" testid="indicator-52w-high" />
          <KV label="52W Low" value={fmt(r52.low)} cls="text-red-300" testid="indicator-52w-low" />
          <KV label="% from High" value={fmtPct(r52.pct_from_high)} cls={dirClass(r52.pct_from_high)} />
          <KV label="% from Low" value={fmtPct(r52.pct_from_low)} cls={dirClass(r52.pct_from_low)} />
        </div>
        {rangePct != null && (
          <div className="mt-3">
            <div className="h-1.5 bg-[#151C2C] relative">
              <div
                className="absolute top-0 bottom-0 left-0 bg-gradient-to-r from-red-500/60 via-slate-500/40 to-emerald-500/60"
                style={{ width: "100%" }}
              />
              <div
                className="absolute top-[-3px] w-1 h-[12px] bg-cyan-400"
                style={{ left: `calc(${rangePct}% - 2px)` }}
              />
            </div>
            <div className="flex justify-between mono text-[9px] text-slate-500 mt-1">
              <span>{fmt(r52.low)}</span>
              <span className="text-cyan-300">@ {fmt(report.price)}</span>
              <span>{fmt(r52.high)}</span>
            </div>
          </div>
        )}
      </Panel>

      {/* Swing S/R */}
      <Panel
        title="Swing S/R"
        icon={<AlertCircle className="h-3.5 w-3.5 text-cyan-400" />}
        testid="panel-swings"
      >
        <div className="space-y-2">
          <div>
            <div className="mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">Resistance (recent highs)</div>
            {(report.swings?.resistance_levels || []).length ? (
              <div className="flex flex-wrap gap-1" data-testid="swing-resistance-levels">
                {report.swings.resistance_levels.slice().reverse().map((p, i) => (
                  <span key={i} className="mono text-[11px] px-2 py-0.5 border border-red-500/30 bg-red-500/5 text-red-300">
                    {fmt(p)}
                  </span>
                ))}
              </div>
            ) : <span className="mono text-[11px] text-slate-500">—</span>}
          </div>
          <div className="pt-2 border-t border-[#151C2C]">
            <div className="mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">Support (recent lows)</div>
            {(report.swings?.support_levels || []).length ? (
              <div className="flex flex-wrap gap-1" data-testid="swing-support-levels">
                {report.swings.support_levels.map((p, i) => (
                  <span key={i} className="mono text-[11px] px-2 py-0.5 border border-emerald-500/30 bg-emerald-500/5 text-emerald-300">
                    {fmt(p)}
                  </span>
                ))}
              </div>
            ) : <span className="mono text-[11px] text-slate-500">—</span>}
          </div>
        </div>
      </Panel>

      {/* Trend structure */}
      <Panel
        title="Trend Structure"
        icon={<TrendingUp className="h-3.5 w-3.5 text-cyan-400" />}
        testid="panel-trend-structure"
      >
        <div className="flex flex-col items-start gap-3">
          <span
            data-testid="indicator-trend-structure-badge"
            className={`mono text-xs px-2.5 py-1 border uppercase tracking-wider ${trendBadgeCls}`}
          >
            {trend.label || "—"}
          </span>
          <div className="space-y-1 w-full">
            <KV
              label="Bars Analyzed"
              value={report.bars_available ?? "—"}
              cls={report.bars_available >= 200 ? "text-slate-100" : "text-amber-300"}
            />
            <div className="mono text-[10px] uppercase tracking-wider text-slate-500 pt-2 border-t border-[#151C2C]">Recent Swing Highs</div>
            <div className="mono text-[10px] text-slate-400 space-y-0.5">
              {(report.swings?.recent_highs || []).slice(-3).reverse().map((s, i) => (
                <div key={i} className="flex justify-between"><span>{new Date(s.t).toISOString().slice(0, 10)}</span><span className="text-red-300">{fmt(s.price)}</span></div>
              ))}
              {(!report.swings?.recent_highs || report.swings.recent_highs.length === 0) && <div className="text-slate-600">—</div>}
            </div>
            <div className="mono text-[10px] uppercase tracking-wider text-slate-500 pt-2 border-t border-[#151C2C]">Recent Swing Lows</div>
            <div className="mono text-[10px] text-slate-400 space-y-0.5">
              {(report.swings?.recent_lows || []).slice(-3).reverse().map((s, i) => (
                <div key={i} className="flex justify-between"><span>{new Date(s.t).toISOString().slice(0, 10)}</span><span className="text-emerald-300">{fmt(s.price)}</span></div>
              ))}
              {(!report.swings?.recent_lows || report.swings.recent_lows.length === 0) && <div className="text-slate-600">—</div>}
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
}
